// Money formatting + parsing, shared by <MoneyInput> and every currency
// display. Amounts are handled as 2-decimal STRINGS end-to-end (matching the
// NUMERIC(x,2) columns and the `\d+(\.\d{1,2})?` API contract), so these
// helpers never introduce floats into stored values.

// Strip a raw input to a typeable numeric string: digits, a single decimal
// point, at most two fraction digits, and an optional leading '-' when
// negatives are allowed (invoice discount lines). Removes '$', commas, letters.
// Runs on every keystroke, so it must tolerate partial input ('', '.', '-').
export function sanitizeMoneyInput(raw: string, allowNegative = false): string {
  const neg = allowNegative && /^\s*-/.test(raw);
  let s = raw.replace(/[^\d.]/g, ''); // digits + dots only
  // Collapse to a single decimal point (keep the first).
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
    // Cap at two fraction digits.
    s = s.slice(0, firstDot + 3);
  }
  return (neg ? '-' : '') + s;
}

// Blur commit: turn whatever's in the box into the canonical stored value.
// Empty / partial ('', '.', '-') → '' so optional fields stay blank and
// required-field validation still fires. Otherwise a 2-decimal string.
export function normalizeMoney(raw: string): string {
  const t = raw.trim();
  if (t === '' || t === '.' || t === '-' || t === '-.') return '';
  const n = Number(t);
  if (!Number.isFinite(n)) return '';
  return n.toFixed(2);
}

// Blurred display INSIDE a MoneyInput — grouped with thousands separators and
// 2 decimals, but WITHOUT the '$' (the component draws '$' as a prefix). Empty
// stays empty. Non-numeric passes through untouched (defensive).
export function formatAmountDisplay(value: string): string {
  const t = value.trim();
  if (t === '') return '';
  const n = Number(t);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Canonical currency display for read-only text ('$1,234.00') — the single
// shared helper for every currency label across the dashboard (invoice totals,
// KPI figures, table cells). `null`/`undefined` → '—' (em-dash); non-numeric
// input passes through unchanged.
export function formatMoney(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

/**
 * Whether an invoice's tax is worth a row of its own.
 *
 * A zero tax is not a fact about the engagement — showing "Tax $0.00" invites
 * the client to check a rate that doesn't apply. Anything non-zero shows,
 * negative adjustments included. An unparseable amount returns TRUE: never
 * silently drop money we couldn't read.
 *
 * Mirrors hasTax in packages/api/src/pdf/helpers.ts, the way formatDiscountPct
 * below mirrors its own counterpart — the invoice detail page and the PDF the
 * client receives must agree on when the row exists.
 */
export function hasTax(amount: string | number | null | undefined): boolean {
  if (amount === null || amount === undefined) return false;
  const n = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(n)) return true;
  return n !== 0;
}

// Discount of a billed unit price vs. its Global baseline, both 2-decimal
// strings (or numbers). Returns an integer percent ONLY when the billed price is
// strictly lower than a positive baseline; otherwise null (equal/higher/
// missing/invalid). This is the single rule for "% off Global", shared by the
// Rates sheet and invoice lines.
export function discountPct(
  unit: string | number | null | undefined,
  global: string | number | null | undefined,
): number | null {
  if (unit == null || global == null) return null;
  const u = typeof unit === 'number' ? unit : Number(unit);
  const g = typeof global === 'number' ? global : Number(global);
  if (!Number.isFinite(u) || !Number.isFinite(g)) return null;
  if (g <= 0 || u >= g) return null;
  return Math.round(((g - u) / g) * 100);
}

// Formatted discount ('14%') or null. Callers render '—' for null.
export function formatDiscountPct(
  unit: string | number | null | undefined,
  global: string | number | null | undefined,
): string | null {
  const p = discountPct(unit, global);
  return p == null ? null : `${p}%`;
}
